import type {
  CoreAssistantMessage,
  CoreToolMessage,
  UIMessage,
  UIMessagePart,
} from 'ai';
import { type ClassValue, clsx } from 'clsx';
import { formatISO } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import type { DBMessage, Document } from '@/lib/db/schema';
import { ChatSDKError, type ErrorCode } from './errors';
import type { ChatMessage, ChatTools, CustomUIDataTypes } from './types';
import { auth } from "@/app/(auth)/auth"

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "https://api-dggs.hartis.org"

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
    this.name = "ApiError"
  }
}

export async function apiFetch(path: string, options: RequestInit = {}) {
  let token: string | null = null
  let refreshToken: string | null = null

  // Client vs server token source
  if (typeof window !== "undefined") {
    token = localStorage.getItem("jwt")
    refreshToken = localStorage.getItem("refresh")
  } else {
    try {
      const session = await auth()
      token = session?.djangoAccess ?? null
      refreshToken = session?.djangoRefresh ?? null
    } catch (err) {
      console.warn("[apiFetch] Server-side auth() failed:", err)
    }
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const url = `${API_BASE}${path}`
  let res: Response

  try {
    res = await fetch(url, { ...options, headers })
  } catch (err) {
    console.error("[apiFetch] Fetch failed:", err)
    throw err
  }

  // Token expired - try to refresh
  if (res.status === 401 && refreshToken && typeof window !== "undefined") {
    console.log("[apiFetch] 401 - attempting token refresh")
    
    try {
      const refreshRes = await fetch(`${API_BASE}/api/token/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: refreshToken }),
      })

      if (refreshRes.ok) {
        const data = await refreshRes.json()
        localStorage.setItem("jwt", data.access)
        if (data.refresh) {
          localStorage.setItem("refresh", data.refresh)
        }

        // Retry original request with new token
        headers.Authorization = `Bearer ${data.access}`
        res = await fetch(url, { ...options, headers })
      } else {
        // Refresh failed - clear tokens and redirect
        localStorage.removeItem("jwt")
        localStorage.removeItem("refresh")
        if (typeof window !== "undefined") {
          window.location.href = "/login"
        }
        throw new Error("Token refresh failed")
      }
    } catch (refreshErr) {
      console.error("[apiFetch] Token refresh error:", refreshErr)
      throw refreshErr
    }
  }

  if (!res.ok) {
    let errorBody: any = null
    try {
      errorBody = await res.text()
    } catch {}

    // FIX 1: Only log actual ERRORS (5xx) or unexpected 4xx. 
    // 404 is usually logic, not a crash.
    if (res.status !== 404 && res.status !== 401) {
      console.error(`[apiFetch] Error ${res.status} on ${path}:`, errorBody)
    }

    // Handle 401 redirect cleanup (Keep your existing logic here)
    if (res.status === 401) {
      console.warn("[apiFetch] Unauthorized after retry — clearing session")
      if (typeof window !== "undefined") {
        localStorage.removeItem("jwt")
        localStorage.removeItem("refresh")
        window.location.href = "/login"
      }
    }

    // FIX 2: Throw a structured error
    throw new ApiError(errorBody || `API error: ${res.status}`, res.status)
  }

  const json = res.status !== 204 ? await res.json() : null
  return json
}

export function isRenderableMessage(m: ChatMessage): boolean {
  // Always show non-assistant messages
  if (m.role !== "assistant") return true;

  const parts = Array.isArray(m.parts) ? m.parts : [];

  const hasText =
    parts.some(p => p?.type === "text" && typeof p.text === "string" && p.text.trim().length > 0);

  const hasReasoning =
    parts.some(p => p?.type === "reasoning" && typeof p.text === "string" && p.text.trim().length > 0);

  const hasToolStuff =
    parts.some(p => String(p?.type).startsWith("tool-") && (p as any).state || (p as any).output || (p as any).input);

  const hasFiles =
    parts.some(p => p?.type === "file");

  // Hide the empty streaming shell until the first delta arrives
  const isTempShell = typeof m.id === "string" && m.id.startsWith("temp-") && parts.length === 0;

  return (hasText || hasReasoning || hasToolStuff || hasFiles) && !isTempShell;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const fetcher = async (url: string) => {
  const response = await fetch(url);

  if (!response.ok) {
    const { code, cause } = await response.json();
    throw new ChatSDKError(code as ErrorCode, cause);
  }

  return response.json();
};

export async function fetchWithErrorHandlers(
  input: RequestInfo | URL,
  init?: RequestInit,
) {
  try {
    const response = await fetch(input, init);

    if (!response.ok) {
      const { code, cause } = await response.json();
      throw new ChatSDKError(code as ErrorCode, cause);
    }

    return response;
  } catch (error: unknown) {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      throw new ChatSDKError('offline:chat');
    }

    throw error;
  }
}

export function getLocalStorage(key: string) {
  if (typeof window !== 'undefined') {
    return JSON.parse(localStorage.getItem(key) || '[]');
  }
  return [];
}

export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

type ResponseMessageWithoutId = CoreToolMessage | CoreAssistantMessage;
type ResponseMessage = ResponseMessageWithoutId & { id: string };

export function getMostRecentUserMessage(messages: UIMessage[]) {
  const userMessages = messages.filter((message) => message.role === 'user');
  return userMessages.at(-1);
}

export function getDocumentTimestampByIndex(
  documents: Document[],
  index: number,
) {
  if (!documents) { return new Date(); }
  if (index > documents.length) { return new Date(); }

  return documents[index].createdAt;
}

export function getTrailingMessageId({
  messages,
}: {
  messages: ResponseMessage[];
}): string | null {
  const trailingMessage = messages.at(-1);

  if (!trailingMessage) { return null; }

  return trailingMessage.id;
}

export function sanitizeText(text: string) {
  return text.replace('<has_function_call>', '');
}


export function convertToUIMessages(messages: DBMessage[]): ChatMessage[] {
  return messages.map((message) => {
    // Convert Django structure (content + attachments) to Vercel parts[]
    const parts: UIMessagePart<CustomUIDataTypes, ChatTools>[] = []

    if (message.content) {
      parts.push({
        type: "text",
        text: message.content,
      } as any)
    }

    if (Array.isArray(message.attachments) && message.attachments.length > 0) {
      for (const att of message.attachments) {
        parts.push({
          type: "file",
          file: att,
        } as any)
      }
    }

    return {
      id: message.id,
      role: message.role as "user" | "assistant" | "system",
      parts,
      metadata: {
        createdAt: formatISO(new Date(message.created_at)),
      },
    }
  })
}


export function getTextFromMessage(message: ChatMessage): string {
  return message.parts
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('');
}
